'use client';

import { useId } from 'react';

import { cn, motion, tv, type UseFormRegisterReturn } from '@/libs';

type CheckboxProps = {
  id?: string;
  value?: string;
  label?: string | React.ReactNode;
  disableTranslation?: boolean;
  size?: 'small' | 'regular' | 'large';
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  field?: Partial<UseFormRegisterReturn>;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

const wrapper = tv({
  base: 'inline-flex items-center gap-2 select-none',
  variants: {
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
  },
});

const iconBox = tv({
  base: 'flex items-center justify-center rounded-sm',
  variants: {
    size: {
      small: 'w-4 h-4',
      regular: 'w-5 h-5',
      large: 'w-6 h-6',
    },
    checked: {
      true: 'bg-primary text-white',
      false: 'bg-white text-transparent',
    },
  },
  defaultVariants: {
    size: 'regular',
    checked: false,
  },
});

const iconSizeMap = {
  small: 10,
  regular: 12,
  large: 14,
} as const;

export const Checkbox = ({
  id: customId,
  value,
  label,
  size = 'regular',
  disabled = false,
  labelClassName,
  className,
  field,
  checked,
  onCheckedChange,
}: CheckboxProps) => {
  const autoId = useId();
  const id = customId || autoId;

  const isChecked = checked ?? false;

  return (
    <label
      htmlFor={id}
      className={cn(wrapper({ disabled }), 'cursor-pointer', className)}
    >
      <input
        id={id}
        value={value ?? ''}
        type="checkbox"
        className="sr-only peer"
        disabled={disabled}
        {...(field
          ? field
          : {
              checked: isChecked,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                onCheckedChange?.(e.target.checked),
            })}
      />

      <i
        className={cn(
          iconBox({ size }),
          'flex-shrink-0 peer-checked:bg-foreground ring-1 ring-inset ring-foreground/10 bg-background peer-checked:text-background transition-colors duration-100',
        )}
      >
        <motion.svg
          width={iconSizeMap[size]}
          height={iconSizeMap[size]}
          viewBox="0 0 12 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          initial={false}
          animate={isChecked ? 'checked' : 'unchecked'}
        >
          <motion.path
            d="M1 5L4.5 8.5L11 1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            variants={{
              checked: {
                pathLength: 1,
                opacity: 1,
                transition: { duration: 0.2, ease: 'easeOut' },
              },
              unchecked: {
                pathLength: 0,
                opacity: 0,
                transition: { duration: 0.2, ease: 'easeIn' },
              },
            }}
          />
        </motion.svg>
      </i>

      {label && (
        <p
          className={cn(
            'text-foreground leading-none text-sm [&>a]:font-semibold [&>a]:transition-colors [&>a:hover]:text-secondary-text',
            labelClassName,
          )}
        >
          {label}
        </p>
      )}
    </label>
  );
};
